<template>
    <BaseFormWrapper :id="id">
        <!-- Eyebrow -->
        <div class="flex h-auto w-full items-start justify-start space-x-2">
            <ToggleSwitch
                v-model="form.hasEyebrow"
                :label="$t('has_eyebrow')"
                :name="`${id}-hasEyebrow`"
            />
            <ToggleSwitch
                v-if="form.hasEyebrow"
                v-model="form.eyebrow.isBadge"
                :label="$t('is_badge')"
                :name="`${id}-isBadge`"
            />
        </div>
        <template v-if="form.hasEyebrow">
            <TextField
                v-model="form.eyebrow.text[activeLocale]"
                :name="`${id}-eyebrow.text`"
                :label="$t('eyebrow_text')"
                help="Eyebrow Text"
                :required="true"
            />
            <SelectField
                v-model="form.eyebrow.color"
                :name="`${id}-eyebrow.color`"
                :label="$t('eyebrow_color')"
                help="Eyebrow Color"
                :options="colorOptions"
            />
            <TextField
                v-model="form.eyebrow.icon"
                :name="`${id}-eyebrow.icon`"
                :label="$t('eyebrow_icon')"
                :help="$t('icon_class_name_example')"
            />
        </template>

        <!-- Title & Description -->
        <TextField
            v-model="form.title[activeLocale]"
            :name="`${id}-title`"
            :label="$t('title')"
            help="Title"
            :required="true"
        />
        <div class="my-4 w-full">
            <label
                class="mb-1 block text-sm font-medium"
                :for="`${id}-description`"
            >
                {{ $t('description') }}
            </label>
            <textarea
                :id="`${id}-description`"
                v-model="form.description[activeLocale]"
                :name="`${id}-description`"
                class="form-input h-32 w-full"
            />
        </div>

        <!-- Layout -->
        <SelectField
            v-model="form.layout"
            :name="`${id}-layout`"
            :label="$t('layout')"
            help="Content layout direction"
            :options="layoutOptions"
        />

        <!-- Image -->
        <div class="border-t border-gray-100 pt-4">
            <ToggleSwitch
                v-model="form.hasImage"
                :label="$t('has_image')"
                :name="`${id}-hasImage`"
            />
            <template v-if="form.hasImage">
                <SelectField
                    v-model="form.imagePosition"
                    :name="`${id}-imagePosition`"
                    :label="$t('image_position')"
                    help="Where the image sits relative to the text"
                    :options="imagePositionOptions"
                    class="mt-3"
                />
                <div class="mt-3">
                    <label class="mb-1 block text-sm font-medium">
                        {{ $t('image') }}
                    </label>
                    <div
                        v-if="form.image"
                        class="space-y-2"
                    >
                        <div
                            class="relative h-56 w-1/2 overflow-hidden rounded-lg bg-slate-100"
                        >
                            <img
                                :src="form.image.asset.url"
                                :alt="form.image.alt"
                                class="h-full w-full object-contain"
                            />
                            <div class="absolute right-2 top-2 flex gap-1">
                                <button
                                    type="button"
                                    class="rounded bg-blue-500 px-2 py-1 text-xs font-medium text-white hover:bg-blue-600"
                                    @click="fileUploadRef?.openModal()"
                                >
                                    {{ $t('update') }}
                                </button>
                                <button
                                    type="button"
                                    class="rounded bg-red-500 px-2 py-1 text-xs font-medium text-white hover:bg-red-600"
                                    @click=";(form.image = null) && imageKey++"
                                >
                                    {{ $t('remove') }}
                                </button>
                            </div>
                        </div>
                        <input
                            v-model="form.image.alt"
                            type="text"
                            class="form-input w-1/2"
                            :placeholder="$t('alt_text')"
                        />
                    </div>
                    <div v-show="!form.image">
                        <FileUploadForm
                            :key="imageKey"
                            ref="fileUploadRef"
                            @select="onImageSelect"
                        />
                    </div>
                </div>
            </template>
        </div>

        <!-- Background -->
        <div class="border-t border-gray-100 pt-4">
            <ToggleSwitch
                v-model="form.hasBackground"
                :label="$t('has_background')"
                :name="`${id}-hasBackground`"
            />
            <template v-if="form.hasBackground">
                <SelectField
                    v-model="form.backgroundType"
                    :name="`${id}-backgroundType`"
                    :label="$t('background_type')"
                    help="Background style"
                    :options="backgroundTypeOptions"
                    class="mt-3"
                />
                <ColorPickerField
                    v-if="form.backgroundType === 'color'"
                    v-model="form.backgroundColor"
                    :name="`${id}-backgroundColor`"
                    label="background_color"
                    help="background_color"
                    class="mt-3"
                />
                <div
                    v-else-if="form.backgroundType === 'image'"
                    class="mt-3"
                >
                    <label class="mb-1 block text-sm font-medium">
                        {{ $t('background_image') }}
                    </label>
                    <div
                        v-if="form.backgroundImage"
                        class="space-y-2"
                    >
                        <div
                            class="relative h-56 w-1/2 overflow-hidden rounded-lg bg-slate-100"
                        >
                            <img
                                :src="form.backgroundImage.asset.url"
                                :alt="form.backgroundImage.alt"
                                class="h-full w-full object-contain"
                            />
                            <div class="absolute right-2 top-2 flex gap-1">
                                <button
                                    type="button"
                                    class="rounded bg-blue-500 px-2 py-1 text-xs font-medium text-white hover:bg-blue-600"
                                    @click="bgFileUploadRef?.openModal()"
                                >
                                    {{ $t('update') }}
                                </button>
                                <button
                                    type="button"
                                    class="rounded bg-red-500 px-2 py-1 text-xs font-medium text-white hover:bg-red-600"
                                    @click="
                                        ;(form.backgroundImage = null) &&
                                            bgImageKey++
                                    "
                                >
                                    {{ $t('remove') }}
                                </button>
                            </div>
                        </div>
                        <input
                            v-model="form.backgroundImage.alt"
                            type="text"
                            class="form-input w-1/2"
                            :placeholder="$t('alt_text')"
                        />
                    </div>
                    <div v-show="!form.backgroundImage">
                        <FileUploadForm
                            :key="bgImageKey"
                            ref="bgFileUploadRef"
                            @select="onBgImageSelect"
                        />
                    </div>
                </div>
            </template>
        </div>
    </BaseFormWrapper>
</template>

<script setup lang="ts">
import type { MediaAsset, PageHeaderBlock } from '@builder/types'
import { defaultPageHeaderBlock } from '@builder/defaults'
import { colorOptions } from '@src/partials/pageBuilder/options'
import SelectField from '@src/components/SelectField.vue'
import TextField from '@src/components/TextField.vue'
import ToggleSwitch from '@src/components/ToggleSwitch.vue'
import ColorPickerField from '@src/components/ColorPickerField.vue'
import { useDebounceFn } from '@vueuse/core'
import { ref, watch } from 'vue'
import BaseFormWrapper from './BaseFormWrapper.vue'
import FileUploadForm from '../FileUploadForm.vue'

const props = defineProps<{
    id: string
    activeLocale: string
    initialData?: PageHeaderBlock | undefined
}>()
const emit = defineEmits(['update:form'])

const fileUploadRef = ref<InstanceType<typeof FileUploadForm> | null>(null)
const bgFileUploadRef = ref<InstanceType<typeof FileUploadForm> | null>(null)
const imageKey = ref(0)
const bgImageKey = ref(0)

const form = ref<PageHeaderBlock>(
    props.initialData
        ? { ...props.initialData }
        : defaultPageHeaderBlock(props.id)
)

const layoutOptions = [
    { label: 'Vertical', value: 'vertical' },
    { label: 'Horizontal', value: 'horizontal' },
]

const imagePositionOptions = [
    { label: 'Left', value: 'left' },
    { label: 'Right', value: 'right' },
    { label: 'Top', value: 'top' },
    { label: 'Bottom', value: 'bottom' },
]

const backgroundTypeOptions = [
    { label: 'Color', value: 'color' },
    { label: 'Image', value: 'image' },
]

const onImageSelect = (asset: MediaAsset) => {
    form.value.image = { asset, alt: '' }
}

const onBgImageSelect = (asset: MediaAsset) => {
    form.value.backgroundImage = { asset, alt: '' }
}

const debouncedEmit = useDebounceFn((newVal: PageHeaderBlock) => {
    emit('update:form', newVal)
}, 1000)

watch(
    form,
    (newVal: PageHeaderBlock) => {
        debouncedEmit(newVal)
    },
    { deep: true }
)
</script>
